import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ClipboardList,
  CalendarClock,
  Eye,
  FileSignature,
  FileText,
  FolderOpen,
  History,
  Info,
  RefreshCw,
} from "lucide-react";
import { Button, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import MenuLateral from "../../Componentes/MenuLateral";
import MenuSuperior from "../../Componentes/MenuSuperior";
import { useOperador } from "../../Hooks/useOperador";
import { assinaturaDigitalService } from "../../Services/assinaturaDigital";
import requestPreSignedUrl from "../../Services/requestPreSignedUrlAws";
import api from "../../Services/api";
import "./styles.css";

const statusLabels = {
  aguardando_conferencia: "Aguardando conferência",
  aprovado_para_assinatura: "Aprovado para assinatura",
  assinado: "Assinado",
  recusado: "Recusado",
  solicitar_ajuste: "Solicitar ajuste",
  erro_assinatura: "Erro na assinatura",
};

const statusOptions = [
  "",
  "aguardando_conferencia",
  "aprovado_para_assinatura",
  "assinado",
  "recusado",
  "solicitar_ajuste",
  "erro_assinatura",
];

const visualizacoes = {
  fila: {
    label: "Fila",
    descricao: "Documentos aguardando conferência, assinatura ou nova tentativa.",
    statuses: [
      "aguardando_conferencia",
      "aprovado_para_assinatura",
      "erro_assinatura",
    ],
  },
  historico: {
    label: "Histórico",
    descricao: "Documentos assinados, recusados ou enviados para ajuste.",
    statuses: ["assinado", "recusado", "solicitar_ajuste"],
  },
};

const formatarData = (value) => {
  if (!value) return "Data não informada";

  const data = new Date(value);
  if (Number.isNaN(data.getTime())) return "Data não informada";

  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const podeSelecionar = (pendencia) => [
  "aguardando_conferencia",
  "aprovado_para_assinatura",
  "erro_assinatura",
].includes(pendencia.statusAssinatura);

const AssinaturaDigital = () => {
  const { IDOperador, nome, permissaoPrincipal } = useOperador();
  const [pendencias, setPendencias] = useState([]);
  const [statusAssinatura, setStatusAssinatura] = useState("");
  const [visualizacao, setVisualizacao] = useState("fila");
  const [loading, setLoading] = useState(false);
  const [conferenciaAberta, setConferenciaAberta] = useState(false);
  const [conferenciaUrl, setConferenciaUrl] = useState("");
  const [conferenciaPendencia, setConferenciaPendencia] = useState(null);
  const [conferenciaLoading, setConferenciaLoading] = useState(false);
  const [certificadoStatus, setCertificadoStatus] = useState(null);
  const [idCertificadoSelecionado, setIdCertificadoSelecionado] = useState("");
  const [selecionados, setSelecionados] = useState(new Set());
  const [processandoLote, setProcessandoLote] = useState(false);
  const [progressoLote, setProgressoLote] = useState(null);
  const loteEmAndamentoRef = useRef(false);
  const listagemSeqRef = useRef(0);

  const pendenciasSelecionaveis = visualizacao === "fila"
    ? pendencias.filter(podeSelecionar)
    : [];
  const todosSelecionados = pendenciasSelecionaveis.length > 0 &&
    pendenciasSelecionaveis.every((pendencia) =>
      selecionados.has(pendencia.IDArquivoAssinaturaPendencia),
    );
  const certificadosAtivos = certificadoStatus?.certificados || [];

  useEffect(() => {
    setIdCertificadoSelecionado((atual) => {
      if (certificadosAtivos.length === 1) {
        return String(certificadosAtivos[0].IDAssinaturaDigitalCertificado);
      }
      return certificadosAtivos.some((certificado) =>
        String(certificado.IDAssinaturaDigitalCertificado) === String(atual),
      ) ? atual : "";
    });
  }, [certificadoStatus]);

  const carregarPendencias = useCallback(async () => {
    if (loteEmAndamentoRef.current) return;
    const sequencia = ++listagemSeqRef.current;
    try {
      setLoading(true);
      const statuses = statusAssinatura
        ? [statusAssinatura]
        : visualizacoes[visualizacao].statuses;
      const pendenciasPorId = new Map();

      for (const status of statuses) {
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const response = await assinaturaDigitalService.listarPendencias({
            page,
            limit: 100,
            statusAssinatura: status,
          });
          const data = Array.isArray(response?.data) ? response.data : [];
          data.forEach((pendencia) => {
            pendenciasPorId.set(
              pendencia.IDArquivoAssinaturaPendencia,
              pendencia,
            );
          });
          hasMore = response?.hasMore === true;
          page += 1;
        }
      }

      if (sequencia !== listagemSeqRef.current) return;
      const lista = Array.from(pendenciasPorId.values());
      setPendencias(lista);
      const idsDisponiveis = new Set(
        lista.filter(podeSelecionar).map((item) => item.IDArquivoAssinaturaPendencia),
      );
      setSelecionados((anteriores) =>
        new Set(Array.from(anteriores).filter((id) => idsDisponiveis.has(id))),
      );
    } catch (error) {
      if (sequencia === listagemSeqRef.current) {
        console.error("Erro ao carregar pendencias de assinatura.", error);
        Swal.fire("Erro", "Nao foi possivel carregar as pendencias.", "error");
      }
    } finally {
      if (sequencia === listagemSeqRef.current) setLoading(false);
    }
  }, [statusAssinatura, visualizacao]);

  useEffect(() => {
    carregarPendencias();
  }, [carregarPendencias]);

  useEffect(() => {
    const atualizarFila = () => {
      if (document.visibilityState === "visible") {
        carregarPendencias();
      }
    };

    window.addEventListener("assinatura:fila-refresh", atualizarFila);

    return () => {
      window.removeEventListener("assinatura:fila-refresh", atualizarFila);
    };
  }, [carregarPendencias]);

  const carregarStatusCertificado = useCallback(async () => {
    try {
      const status = await assinaturaDigitalService.buscarCertificado();
      setCertificadoStatus(status || null);
    } catch (error) {
      console.error("Erro ao carregar status do certificado.", error);
      setCertificadoStatus(null);
    }
  }, []);

  useEffect(() => {
    carregarStatusCertificado();
  }, [carregarStatusCertificado]);

  const obterArquivoParaConferencia = async (pendencia) => {
    const detalhe = await assinaturaDigitalService.buscarPendencia(
      pendencia.IDArquivoAssinaturaPendencia,
    );
    const arquivo = detalhe || pendencia;
    let bucketName = arquivo.nomeBucketAws;

    if (!bucketName && arquivo.IDDiretorio) {
      const processoResponse = await api.get(
        `/processo/diretorio/${arquivo.IDDiretorio}`,
      );
      bucketName = processoResponse?.data?.nomeBucketAws;
    }

    if (!bucketName || !arquivo.caminhoArquivoOriginal) {
      throw new Error("Arquivo original não localizado.");
    }

    const urlArquivo = await requestPreSignedUrl(
      permissaoPrincipal,
      "getObject",
      bucketName,
      arquivo.caminhoArquivoOriginal,
      arquivo.nomeArquivo || pendencia.nomeArquivo,
      "inline",
    );

    if (!urlArquivo || typeof urlArquivo !== "string") {
      throw new Error("URL do arquivo não localizada.");
    }

    return { arquivo, urlArquivo };
  };

  const abrirConferencia = async (pendencia) => {
    setConferenciaAberta(true);
    setConferenciaPendencia(pendencia);
    setConferenciaUrl("");

    try {
      setConferenciaLoading(true);
      await carregarStatusCertificado();
      const { arquivo, urlArquivo } = await obterArquivoParaConferencia(pendencia);
      setConferenciaPendencia(arquivo);
      setConferenciaUrl(urlArquivo);
    } catch (error) {
      console.error("Erro ao abrir arquivo para conferência.", error);
      setConferenciaAberta(false);
      Swal.fire(
        "Arquivo indisponível",
        "Não foi possível abrir o arquivo para conferência.",
        "warning",
      );
    } finally {
      setConferenciaLoading(false);
    }
  };

  const fecharConferencia = () => {
    setConferenciaAberta(false);
    setConferenciaUrl("");
    setConferenciaPendencia(null);
  };

  const podeAjustarConferencia =
    conferenciaPendencia &&
    !["assinado", "recusado"].includes(conferenciaPendencia.statusAssinatura);

  const podeAssinarConferencia =
    conferenciaPendencia &&
    [
      "aguardando_conferencia",
      "aprovado_para_assinatura",
      "erro_assinatura",
    ].includes(
      conferenciaPendencia.statusAssinatura,
    );

  const assinaturaLiberada = certificadoStatus?.assinaturaDisponivel === true;
  const mensagemAssinaturaIndisponivel =
    certificadoStatus?.mensagemAssinatura ||
    "A conferência já pode ser feita, mas a assinatura ainda não está liberada no servidor.";

  const obterCertificadoSelecionado = (status) => {
    const certificados = status?.certificados || [];
    if (certificados.length <= 1) {
      return certificados[0]?.IDAssinaturaDigitalCertificado;
    }
    const selecionado = Number(idCertificadoSelecionado);
    return certificados.some((certificado) =>
      Number(certificado.IDAssinaturaDigitalCertificado) === selecionado,
    ) ? selecionado : null;
  };

  const aprovarEAssinar = async () => {
    if (!conferenciaPendencia) return;

    try {
      setConferenciaLoading(true);
      const statusAtual = await assinaturaDigitalService.buscarCertificado();
      setCertificadoStatus(statusAtual || null);

      if (statusAtual?.assinaturaDisponivel !== true) {
        Swal.fire(
          "Assinatura ainda não liberada",
          statusAtual?.mensagemAssinatura || mensagemAssinaturaIndisponivel,
          "info",
        );
        return;
      }

      const IDAssinaturaDigitalCertificado = obterCertificadoSelecionado(statusAtual);
      if (IDAssinaturaDigitalCertificado === null) {
        Swal.fire("Escolha o certificado", "Selecione o certificado A1 para assinar.", "warning");
        return;
      }

      if (conferenciaPendencia.statusAssinatura === "aguardando_conferencia") {
        await assinaturaDigitalService.aprovarPendencia(
          conferenciaPendencia.IDArquivoAssinaturaPendencia,
        );
      }

      await assinaturaDigitalService.assinarPendencia(
        conferenciaPendencia.IDArquivoAssinaturaPendencia,
        { IDAssinaturaDigitalCertificado },
      );
      fecharConferencia();
      await carregarPendencias();
      Swal.fire(
        "Sucesso",
        "Arquivo assinado. Ele saiu da fila e continua disponível no histórico.",
        "success",
      );
    } catch (error) {
      console.error("Erro ao aprovar e assinar pendência.", error);
      Swal.fire(
        "Aviso",
        error.response?.data?.errors?.default ||
          "Não foi possível concluir a assinatura.",
        "warning",
      );
    } finally {
      setConferenciaLoading(false);
    }
  };

  const alternarSelecao = (id) => {
    setSelecionados((anteriores) => {
      const proximos = new Set(anteriores);
      if (proximos.has(id)) proximos.delete(id);
      else proximos.add(id);
      return proximos;
    });
  };

  const alternarTodos = () => {
    setSelecionados(todosSelecionados
      ? new Set()
      : new Set(pendenciasSelecionaveis.map((item) => item.IDArquivoAssinaturaPendencia)));
  };

  const assinarSelecionados = async () => {
    if (loteEmAndamentoRef.current || loading) return;

    const itens = pendenciasSelecionaveis.filter((item) =>
      selecionados.has(item.IDArquivoAssinaturaPendencia),
    );
    if (itens.length === 0) return;

    const confirmacao = await Swal.fire({
      title: `Assinar ${itens.length} documento${itens.length === 1 ? "" : "s"}?`,
      text: "Os documentos selecionados serao aprovados quando necessario e assinados em sequencia, sem abrir a conferencia individual.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirmar assinatura",
      cancelButtonText: "Cancelar",
    });
    if (!confirmacao.isConfirmed || loteEmAndamentoRef.current) return;

    loteEmAndamentoRef.current = true;
    listagemSeqRef.current += 1;
    setProcessandoLote(true);
    setProgressoLote({ concluido: 0, total: itens.length });
    let assinados = 0;
    const falhas = [];

    try {
      const status = await assinaturaDigitalService.buscarCertificado();
      setCertificadoStatus(status || null);
      if (status?.assinaturaDisponivel !== true) {
        await Swal.fire(
          "Assinatura ainda nao liberada",
          status?.mensagemAssinatura || mensagemAssinaturaIndisponivel,
          "info",
        );
        return;
      }

      const IDAssinaturaDigitalCertificado = obterCertificadoSelecionado(status);
      if (IDAssinaturaDigitalCertificado === null) {
        await Swal.fire("Escolha o certificado", "Selecione o certificado A1 para o lote.", "warning");
        return;
      }

      for (const [indice, item] of itens.entries()) {
        try {
          if (item.statusAssinatura === "aguardando_conferencia") {
            await assinaturaDigitalService.aprovarPendencia(
              item.IDArquivoAssinaturaPendencia,
              "lote",
            );
          }
          await assinaturaDigitalService.assinarPendencia(
            item.IDArquivoAssinaturaPendencia,
            { origem: "lote", IDAssinaturaDigitalCertificado },
          );
          assinados += 1;
        } catch (error) {
          falhas.push({
            nome: item.nomeArquivo || `Pendencia ${item.IDArquivoAssinaturaPendencia}`,
            motivo: error.response?.data?.errors?.default || error.message || "Erro desconhecido",
          });
        }
        setProgressoLote({ concluido: indice + 1, total: itens.length });
      }

      const resumo = [
        `${assinados} assinado${assinados === 1 ? "" : "s"}; ${falhas.length} com erro.`,
        ...falhas.slice(0, 10).map((falha) => `${falha.nome}: ${falha.motivo}`),
        ...(falhas.length > 10 ? [`Mais ${falhas.length - 10} erro(s) nao exibidos.`] : []),
      ].join("\n");
      await Swal.fire(
        falhas.length ? "Assinatura parcialmente concluida" : "Assinatura concluida",
        resumo,
        falhas.length ? "warning" : "success",
      );
    } catch (error) {
      console.error("Erro ao iniciar assinatura em lote.", error);
      await Swal.fire("Erro", "Nao foi possivel iniciar a assinatura em lote.", "error");
    } finally {
      loteEmAndamentoRef.current = false;
      setProcessandoLote(false);
      setProgressoLote(null);
      await carregarPendencias();
    }
  };

  const executarAjusteDaConferencia = async (acao, titulo) => {
    if (!conferenciaPendencia) return;

    const { value: justificativa } = await Swal.fire({
      title: titulo,
      input: "textarea",
      inputLabel: "Justificativa",
      inputPlaceholder: "Descreva o motivo",
      showCancelButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
      inputValidator: (value) =>
        !value || value.trim().length < 3
          ? "Informe uma justificativa."
          : null,
    });

    if (!justificativa) return;

    try {
      setConferenciaLoading(true);
      await acao(
        conferenciaPendencia.IDArquivoAssinaturaPendencia,
        justificativa,
      );
      fecharConferencia();
      await carregarPendencias();
      Swal.fire("Sucesso", "Pendência atualizada com sucesso.", "success");
    } catch (error) {
      console.error("Erro ao atualizar pendência.", error);
      Swal.fire("Erro", "Não foi possível concluir a ação.", "error");
    } finally {
      setConferenciaLoading(false);
    }
  };

  return (
    <div className="menu-container-menu assinatura-page">
      <MenuLateral />
      <div id="painel_principal_menu" align="center">
        <div className="page-shell assinatura-shell">
          <MenuSuperior userId={String(IDOperador).padStart(6, "0")} userName={nome} />

          <section className="assinatura-header">
            <div>
              <span>ICP-Brasil</span>
              <h1>Assinaturas Digitais</h1>
              <p>{visualizacoes[visualizacao].descricao}</p>
            </div>
            <div className="assinatura-actions">
              <select
                value={statusAssinatura}
                onChange={(event) => {
                  setStatusAssinatura(event.target.value);
                  setSelecionados(new Set());
                }}
                disabled={processandoLote}
              >
                {statusOptions
                  .filter(
                    (status) =>
                      !status ||
                      visualizacoes[visualizacao].statuses.includes(status),
                  )
                  .map((status) => (
                  <option key={status || "todos"} value={status}>
                    {status ? statusLabels[status] : "Todos os status"}
                  </option>
                  ))}
              </select>
              <button type="button" onClick={carregarPendencias} disabled={processandoLote}>
                <RefreshCw size={16} />
                Atualizar
              </button>
            </div>
          </section>

          <div className="assinatura-tabs" role="tablist" aria-label="Visão da assinatura digital">
            <button
              type="button"
              className={visualizacao === "fila" ? "is-active" : ""}
              onClick={() => {
                setVisualizacao("fila");
                setStatusAssinatura("");
                setSelecionados(new Set());
              }}
              disabled={processandoLote}
            >
              <ClipboardList size={16} />
              Fila
            </button>
            <button
              type="button"
              className={visualizacao === "historico" ? "is-active" : ""}
              onClick={() => {
                setVisualizacao("historico");
                setStatusAssinatura("");
                setSelecionados(new Set());
              }}
              disabled={processandoLote}
            >
              <History size={16} />
              Histórico
            </button>
          </div>

          <section className="assinatura-list">
            {loading && <div className="assinatura-loading">Carregando...</div>}

            {visualizacao === "fila" && pendenciasSelecionaveis.length > 0 && (
              <div className="assinatura-bulk-bar">
                <button
                  type="button"
                  className="assinatura-select-all"
                  onClick={alternarTodos}
                  disabled={loading || processandoLote}
                >
                  {todosSelecionados ? "Desmarcar todos" : "Selecionar todos"}
                </button>
                <span>{selecionados.size} de {pendenciasSelecionaveis.length} selecionado(s)</span>
                {certificadosAtivos.length > 1 && (
                  <label className="assinatura-cert-choice">
                    Certificado A1
                    <select
                      value={idCertificadoSelecionado}
                      onChange={(event) => setIdCertificadoSelecionado(event.target.value)}
                      disabled={processandoLote}
                    >
                      <option value="">Escolha o certificado</option>
                      {certificadosAtivos.map((certificado) => (
                        <option
                          key={certificado.IDAssinaturaDigitalCertificado}
                          value={certificado.IDAssinaturaDigitalCertificado}
                        >
                          {certificado.certificadoTitular || "A1"} (#{certificado.IDAssinaturaDigitalCertificado})
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <button
                  type="button"
                  className="assinatura-bulk-sign"
                  onClick={assinarSelecionados}
                  disabled={loading || processandoLote || selecionados.size === 0}
                >
                  <FileSignature size={16} />
                  Assinar selecionados
                </button>
                {progressoLote && (
                  <span role="status">
                    Processando {progressoLote.concluido} de {progressoLote.total}
                  </span>
                )}
              </div>
            )}

            {!loading && pendencias.length === 0 && (
              <div className="assinatura-empty">
                <FileSignature size={28} />
                <span>
                  {visualizacao === "fila"
                    ? "Nenhum documento aguardando assinatura."
                    : "Nenhum documento encontrado no histórico."}
                </span>
              </div>
            )}

            {pendencias.map((pendencia) => (
              <article
                key={pendencia.IDArquivoAssinaturaPendencia}
                className={`assinatura-item is-${pendencia.statusAssinatura}${visualizacao === "fila" && podeSelecionar(pendencia) ? " has-selection" : ""}`}
              >
                {visualizacao === "fila" && podeSelecionar(pendencia) && (
                  <label className="assinatura-item__selection">
                    <input
                      type="checkbox"
                      checked={selecionados.has(pendencia.IDArquivoAssinaturaPendencia)}
                      onChange={() => alternarSelecao(pendencia.IDArquivoAssinaturaPendencia)}
                      disabled={processandoLote || loading}
                      aria-label={`Selecionar ${pendencia.nomeArquivo || "documento"} para assinatura`}
                    />
                  </label>
                )}
                <div className="assinatura-item__content">
                  <div className="assinatura-item__top">
                    <span className="assinatura-status-badge">
                      {statusLabels[pendencia.statusAssinatura] ||
                        pendencia.statusAssinatura}
                    </span>
                  </div>

                  <div className="assinatura-item__title">
                    <FileText size={18} />
                    <strong title={pendencia.nomeArquivo}>
                      {pendencia.nomeArquivo || "Arquivo não informado"}
                    </strong>
                  </div>

                  <div className="assinatura-item__meta">
                    <span title={pendencia.nomeDiretorio || "Diretório não informado"}>
                      <FolderOpen size={15} />
                      <b>Diretório</b>
                      {pendencia.nomeDiretorio || "Diretório não informado"}
                    </span>
                    <span>
                      <CalendarClock size={15} />
                      <b>Solicitado em</b>
                      {formatarData(pendencia.dataSolicitacao)}
                    </span>
                  </div>

                  {pendencia.statusAssinatura === "erro_assinatura" && (
                    <small className="assinatura-item__warning">
                      Houve erro na última tentativa. Abra o documento para tentar novamente.
                    </small>
                  )}

                  {pendencia.statusAssinatura === "assinado" && (
                    <small className="assinatura-item__success">
                      Assinatura concluída. O arquivo permanece com o mesmo nome na pasta.
                    </small>
                  )}

                  {pendencia.statusAssinatura !== "erro_assinatura" &&
                    pendencia.statusAssinatura !== "assinado" && (
                    <small>
                    {statusLabels[pendencia.statusAssinatura] ||
                      pendencia.statusAssinatura}
                    </small>
                    )}
                </div>

                <div className="assinatura-item__actions">
                  <button
                    type="button"
                    className="is-secondary"
                    onClick={() => abrirConferencia(pendencia)}
                    disabled={processandoLote}
                  >
                    <Eye size={15} />
                    {visualizacao === "fila" ? "Conferir documento" : "Ver documento"}
                  </button>
                </div>
              </article>
            ))}
          </section>

          <Modal
            show={conferenciaAberta}
            onHide={fecharConferencia}
            centered
            size="xl"
            dialogClassName="assinatura-conferencia-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title>Conferência para assinatura</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="assinatura-conferencia-info">
                <strong>{conferenciaPendencia?.nomeArquivo}</strong>
                <span>
                  {conferenciaPendencia?.nomeDiretorio ||
                    "Diretório não informado"}
                </span>
              </div>

              {certificadosAtivos.length > 1 && (
                <label className="assinatura-cert-choice">
                  Certificado A1 para esta assinatura
                  <select
                    value={idCertificadoSelecionado}
                    onChange={(event) => setIdCertificadoSelecionado(event.target.value)}
                    disabled={conferenciaLoading}
                  >
                    <option value="">Escolha o certificado</option>
                    {certificadosAtivos.map((certificado) => (
                      <option
                        key={certificado.IDAssinaturaDigitalCertificado}
                        value={certificado.IDAssinaturaDigitalCertificado}
                      >
                        {certificado.certificadoTitular || "A1"} (#{certificado.IDAssinaturaDigitalCertificado})
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {!assinaturaLiberada && (
                <div className="assinatura-provider-alert">
                  <Info size={17} />
                  <div>
                    <strong>Conferência liberada</strong>
                    <span>{mensagemAssinaturaIndisponivel}</span>
                  </div>
                </div>
              )}

              <div className="assinatura-preview">
                {conferenciaLoading && (
                  <div className="assinatura-loading">Carregando arquivo...</div>
                )}
                {!conferenciaLoading && conferenciaUrl && (
                  <iframe
                    title="Arquivo para conferência"
                    src={`${conferenciaUrl}#toolbar=1&navpanes=1&scrollbar=1&zoom=page-fit`}
                  />
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              {podeAjustarConferencia && (
                <>
                  <Button
                    variant="secondary"
                    className="assinatura-modal-btn is-muted"
                    onClick={() =>
                      executarAjusteDaConferencia(
                        assinaturaDigitalService.solicitarAjustePendencia,
                        "Solicitar ajuste",
                      )
                    }
                    disabled={conferenciaLoading}
                  >
                    Solicitar ajuste
                  </Button>
                  <Button
                    variant="secondary"
                    className="assinatura-modal-btn is-danger"
                    onClick={() =>
                      executarAjusteDaConferencia(
                        assinaturaDigitalService.recusarPendencia,
                        "Recusar assinatura",
                      )
                    }
                    disabled={conferenciaLoading}
                  >
                    Recusar
                  </Button>
                </>
              )}
              {podeAssinarConferencia && (
                <Button
                  variant="primary"
                  className="assinatura-modal-btn"
                  onClick={aprovarEAssinar}
                  disabled={conferenciaLoading || !conferenciaUrl}
                  title={
                    !assinaturaLiberada
                      ? "Clique para verificar se a assinatura ja foi liberada no servidor"
                      : undefined
                  }
                >
                  <FileSignature size={16} />
                  {conferenciaPendencia?.statusAssinatura === "erro_assinatura"
                    ? "Tentar novamente"
                    : conferenciaPendencia?.statusAssinatura ===
                        "aprovado_para_assinatura"
                      ? "Assinar"
                      : "Aprovar e assinar"}
                </Button>
              )}
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default AssinaturaDigital;
